'use client';

/**
 * ОКНО БОТА В БРАУЗЕРЕ — ТЕ ЖЕ СТРАНИЦЫ, ЧТО В ПРИЛОЖЕНИИ (ТЗ ~/projects/WEB-LOOK-CLAIM-1-TZ.md,
 * претензия владельца 01.10.2026). Мозг окна (`bot-window-native.tsx`) собирает готовые
 * страницы; в приложении их рисует натив (`BotWindowScreen.swift`), в браузере — этот файл,
 * из того же списка блоков (`lib/stats-web.ts`). Своих данных, слов и чисел здесь нет.
 *
 * Состав листа — зеркало родного: шапка в один ряд («×», титул, «⋯»), герой, пузыри строк по
 * 52 на полях страницы, подписи разделов над пузырями и сноски под ними. Строки — общая
 * строка списка набора (`KListRow` = `AccountFieldRow` приложения).
 *
 * INVARIANT(scriptera): вид строки решает её `kind` из протокола строк «Аккаунта», второго
 * протокола у окна нет. Незнакомый вид блока или строки НЕ рисуется (как у старой сборки
 * приложения) — выдумывать ему разметку нельзя. Канон: ~/projects/WEB-LOOK-CLAIM-1-TZ.md.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import {
  KButton,
  KDots,
  KInput,
  KListBlock,
  KListCheck,
  KListField,
  KListGroup,
  KEmpty,
  KListNote,
  KListRow,
  KListSection,
  KListValueIcon,
  KBareButton,
  KSegment,
  KSwitch,
  KTextarea,
} from '@scriptera/ui/kit';
import { PopMenu } from '@scriptera/ui';
import type {
  NativeAccountField,
  NativeAccountHero,
  NativeStatsBlock,
  NativeStatsEvent,
  NativeStatsFolders,
  NativeStatsPage,
  NativeStatsRun,
} from '@/lib/native-app';
import { statsWebNow, useStatsWeb } from '@/lib/stats-web';
import { ChannelIcon, channelHex } from './bot-templates';
import { DayBars } from './day-line';
import { FClearField, FFormError } from './form-kit';
import { useSlidePanel } from './native-layer-host';
import { isAppClient } from './layout-mode';
import { askPrompt } from './ask';
import { SettingsHero } from './settings-hero';
import { Shape, Sk, useShapeRecord } from './skeleton';
import { IconContacts } from './icons';
import { FeedBubbles } from './messages-dialog';
import { useBotWindowBubbles } from '@/lib/bot-window-bubbles';

/** Ключ корневой страницы окна — тот же, что у мозга. */
const ROOT = 'root';
const NO_RECT = { x: 0, y: 0, w: 0, h: 0 };

/** Меню «⋯» шапки в браузере: состав и рука пунктов — раздела (scenarios.tsx, cardMenuOf). */
type PopMenuSections = Parameters<typeof PopMenu>[0]['sections'];
export interface BotWindowWebMenu {
  sections: PopMenuSections;
  onPick: (key: string) => void;
  label: string;
}
let webMenu: (() => BotWindowWebMenu | null) | null = null;
/** Раздел кладёт сюда меню бота, окно которого открыто в браузере (null — меню нет). */
export function setBotWindowWebMenu(fn: (() => BotWindowWebMenu | null) | null): void {
  webMenu = fn;
  bumpBotWindowWebMenu();
}
/** Снять меню, только если стоит именно это (ТЗ WEB-CAUSES О7): уход одного списка не
 *  обнуляет меню окна, которое поставил другой. */
export function clearBotWindowWebMenu(fn: () => BotWindowWebMenu | null): void {
  if (webMenu !== fn) return;
  webMenu = null;
  bumpBotWindowWebMenu();
}
/* «⋯» ПОЯВЛЯЕТСЯ, КАК ТОЛЬКО РАЗДЕЛ УЗНАЛ КАРТОЧКУ (сверка 03.10, кадр iOS B07-botwin): окно,
   открытое адресом (/app/bots/<id>/stats, перезагрузка), рисовало шапку раньше, чем список
   ботов собрал карточки, — меню отдавало null, и в правом слоте оставалась кнопка периода
   «Фильтр». Раздел дёргает `bumpBotWindowWebMenu` на каждую смену карточек, шапка
   перечитывает меню. */
const menuListeners = new Set<() => void>();
export function bumpBotWindowWebMenu(): void {
  for (const l of menuListeners) l();
}
function useMenuTick(): number {
  const [n, setN] = useState(0);
  useEffect(() => {
    const l = (): void => setN((v) => v + 1);
    menuListeners.add(l);
    return () => {
      menuListeners.delete(l);
    };
  }, []);
  return n;
}

// Сколько раз смонтировано тело страницы с этим ключом: уход тела = человек закрыл лист
// (крестик, свайп, «назад» браузера). Счётчик нужен из-за двойного монтирования в разработке.
const mounted = new Map<string, number>();

const tap = (key: string): void => statsWebNow()?.onTap(key, NO_RECT);
const set = (key: string, value: string): void => statsWebNow()?.onSet(key, value);

/**
 * КУДА ВЕДЁТ ЗАКРЫТИЕ КОРНЯ ОКНА (пакет Д1.1, владелец 04.10.2026). Окно бота закрывается в
 * список «Ботов»; окно РАССЫЛКИ на широком экране открыто из раздела «Рассылки» (своё место в
 * левом меню) — закрытие ведёт обратно в него. Ставит раздел, который открыл окно (scenarios.tsx).
 */
let rootCloseHref = '/app/bots';
export function setBotWindowCloseHref(href: string): void {
  rootCloseHref = href;
}

/** Хозяин: стоит один на кабинет (shell.tsx). Открывает и снимает листы по стопке мозга. */
export function BotWindowWebHost() {
  const st = useStatsWeb();
  const panel = useSlidePanel();
  const openRef = useRef<string[]>([]);
  const stack = st?.stack ?? null;
  const stackKey = stack ? stack.join('|') : '';
  const titles = st ? st.pages.map((p) => `${p.key}=${p.title}`).join('|') : '';

  useEffect(() => {
    const now = statsWebNow();
    const want = now?.stack ?? [];
    // Снять то, чего у мозга больше нет (сверху вниз).
    while (openRef.current.length > 0) {
      const top = openRef.current[openRef.current.length - 1]!;
      if (want.includes(top)) break;
      openRef.current = openRef.current.slice(0, -1);
      if ((mounted.get(top) ?? 0) > 0) {
        // Последняя страница окна бота снята — окно закрывается ЦЕЛИКОМ.
        if (openRef.current.length === 0) panel.closeAll();
        else panel.back();
      }
    }
    // Открыть недостающее (снизу вверх). Отдельной задачей: открытие листа зовёт flushSync.
    const missing = want.filter((k) => !openRef.current.includes(k));
    if (missing.length === 0) return;
    const timer = window.setTimeout(() => {
      for (const key of missing) {
        const page = statsWebNow()?.pages.find((p) => p.key === key);
        if (!page || openRef.current.includes(key)) continue;
        openRef.current = [...openRef.current, key];
        panel.open(pagePayload(page));
      }
    }, 0);
    return () => window.clearTimeout(timer);
    // panel — стабильная рука хоста.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stackKey]);

  // Титул верхнего листа доезжает позже данных (имя бота из списка) — правим на месте.
  useEffect(() => {
    const now = statsWebNow();
    const top = now?.stack[now.stack.length - 1];
    const page = top ? now?.pages.find((p) => p.key === top) : null;
    if (page && openRef.current.includes(page.key)) panel.update({ title: page.title });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [titles]);
  // «⋯» корня тоже доезжает позже (страница-заглушка меню не несёт): встал признак — ставим
  // правый край шапки (тумблер подключит датчик строки «Включён», Б12).
  const rootMenu = !!st?.pages.find((p) => p.key === ROOT)?.menu;
  useEffect(() => {
    const now = statsWebNow();
    if (
      !rootMenu ||
      !now ||
      now.stack[now.stack.length - 1] !== ROOT ||
      !openRef.current.includes(ROOT)
    )
      return;
    panel.update({ headerRight: <HeadRight pinned={false} /> });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rootMenu]);

  // Ушло тело листа — сообщить мозгу (см. PageBody) и забыть ключ.
  useEffect(() => {
    const gone = (e: Event): void => {
      const key = (e as CustomEvent<string>).detail;
      openRef.current = openRef.current.filter((k) => k !== key);
    };
    window.addEventListener(GONE_EVENT, gone);
    return () => window.removeEventListener(GONE_EVENT, gone);
  }, []);
  return null;
}

const GONE_EVENT = 'scr-botwin-gone';

/** Чем открыть страницу: корень — лист окна бота с адресом, остальное — стопкой над ним. */
function pagePayload(page: NativeStatsPage) {
  const root = page.key === ROOT;
  const act = page.action;
  return {
    title: page.title,
    sheet: true as const,
    winKey: root ? 'bot-window' : `bot-window:${page.key}`,
    // Корень — вертикальная шторка с адресом. Колода, ручка, тень и плёнка под ней — закон хоста
    // (drawer.tsx, ТЗ WEB-CAUSES М87/М92): прежний `deck: false` (М82) снят владельцем 07.10.2026.
    ...(root ? { closeHref: rootCloseHref } : {}),
    // Страница над окном («Создать дубликат», `close`) — НОВЫЙ лист поверх окна, как «Название»/«Папка»:
    // слева «×», снимает этот лист (кадр iOS B12-botwin-dup; ТЗ WEB-CAUSES М83, доработка ФИНАЛ-2).
    ...(!root && page.close ? { lead: 'close' as const } : {}),
    ...(page.half ? { sheetDetent: 0.5 } : {}),
    ...(page.menu ? { headerRight: root ? <HeadRight pinned={false} /> : <HeadMenu /> } : {}),
    ...(act
      ? { actionLabel: act.label, actionIcon: 'checkmark', onAction: () => tap(act.key) }
      : {}),
    body: <PageBody pageKey={page.key} />,
  };
}

/** Кружок «⋯» шапки: то же меню, что у карточки в списке (в приложении — родное меню). */
function HeadMenu() {
  // Подписка на страницы окна: после «Выключить» строки страницы меняются — и меню
  // перечитывается тем же кадром, иначе первый пункт оставался «Выключить» (К3.22).
  useStatsWeb();
  useMenuTick();
  const m = webMenu?.() ?? null;
  if (!m) return null;
  return (
    <PopMenu
      ariaLabel={m.label}
      triggerClassName="dr-act dr-more"
      /* «⋯» — один глиф набора (KDots: SF `ellipsis` 13 semibold, П1б 05.10). */
      trigger={<KDots />}
      sections={m.sections}
      onPick={m.onPick}
      float
    />
  );
}

/** Ключ строки «Включён» корня (bot-window-native: `bot:on`). */
const BOT_ON_KEY = 'bot:on';

/** ТУМБЛЕР В ШАПКЕ (ТЗ WEB-PARITY-3 Б12, кадр iOS B07-botwin-scrolled): когда строка
 *  «Включён» уехала под шапку листа, её тумблер встаёт в шапку рядом с «⋯» — бот можно
 *  выключить, не листая назад. Состояние — то же поле мозга окна, второго счёта нет. */
function HeadSwitch() {
  const st = useStatsWeb();
  const f = st?.pages
    .find((p) => p.key === ROOT)
    ?.blocks.flatMap((b) => b.fields ?? [])
    .find((x) => x.key === BOT_ON_KEY);
  if (!f) return null;
  // Тумблер ещё ждёт ответа сервера (SERVER_TRUTH) — место пустое того же размера.
  if (f.wait) return <span className="bw-headsw bw-sw-wait" aria-hidden />;
  return (
    <KSwitch
      className="bw-headsw"
      checked={!!f.on}
      disabled={!!f.disabled}
      ariaLabel={f.title}
      onChange={(v) => set(f.key, v ? '1' : '0')}
    />
  );
}

/** Правый край шапки корня: тумблер (только пока строка «Включён» под шапкой) + «⋯». */
function HeadRight({ pinned }: { pinned: boolean }) {
  return (
    <div className={`bw-headr${pinned ? ' is-pinned' : ''}`}>
      {pinned ? <HeadSwitch /> : null}
      <HeadMenu />
    </div>
  );
}

/** Строка «Включён» корня: следит, ушла ли она под шапку, и переставляет тумблер в шапку.
 *  Браузер (телефон и панель 460 широкого экрана — Д1.4): в приложении шапку рисует натив.
 *  Переставляет — лишь когда корень и есть верхний лист (стопка над ним
 *  свой правый край не теряет). */
function BotOnRow({ children }: { children: ReactNode }) {
  const panel = useSlidePanel();
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    // Панель 460 на широком экране — тот же телефон (Д1.4): тумблер переезжает в шапку и там.
    if (!el || isAppClient()) return;
    const drawer = el.closest('.drawer');
    const body = drawer?.querySelector<HTMLElement>('.dr-body') ?? null;
    const head = drawer?.querySelector<HTMLElement>('.dr-head') ?? null;
    if (!body) return;
    let pinned = false;
    const io = new IntersectionObserver(
      ([e]) => {
        if (!e) return;
        const next = !e.isIntersecting && e.boundingClientRect.top < (e.rootBounds?.top ?? 0) + 1;
        if (next === pinned) return;
        pinned = next;
        const now = statsWebNow();
        if ((now?.stack[now.stack.length - 1] ?? ROOT) !== ROOT) return;
        panel.update({ headerRight: <HeadRight pinned={pinned} /> });
      },
      { root: body, rootMargin: `-${head?.offsetHeight ?? 0}px 0px 0px 0px`, threshold: 0 },
    );
    // Обёртка — display: contents (разделители группы считают строки соседями), своего
    // прямоугольника у неё нет: наблюдаем саму строку.
    io.observe(el.firstElementChild ?? el);
    return () => io.disconnect();
    // panel — стабильная рука хоста.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div ref={ref} className="bw-onrow">
      {children}
    </div>
  );
}

/** Тело страницы: блоки по порядку. Читает свежие страницы само — лист не переоткрывается. */
/** Последний вид каждой страницы. Лист закрывают — мозг уходит сразу (адрес сменился), а
 *  лист ещё едет вниз: без этой копии он уезжал ПУСТЫМ, с одной шапкой (покадровая запись
 *  закрытия крестиком, 01.10.2026). Копия живёт до ухода листа с экрана. */
const lastPages = new Map<string, NativeStatsPage>();

function PageBody({ pageKey }: { pageKey: string }) {
  const st = useStatsWeb();
  const live = st?.pages.find((p) => p.key === pageKey) ?? null;
  useEffect(() => {
    if (live) lastPages.set(pageKey, live);
  }, [live, pageKey]);
  const page = live ?? lastPages.get(pageKey) ?? null;
  // Лист ушёл (крестик, свайп, «назад») — мозг узнаёт так же, как от приложения (statsBack).
  useEffect(() => {
    mounted.set(pageKey, (mounted.get(pageKey) ?? 0) + 1);
    return () => {
      mounted.set(pageKey, (mounted.get(pageKey) ?? 1) - 1);
      window.setTimeout(() => {
        if ((mounted.get(pageKey) ?? 0) > 0) return;
        lastPages.delete(pageKey);
        window.dispatchEvent(new CustomEvent(GONE_EVENT, { detail: pageKey }));
        const now = statsWebNow();
        if (now?.stack.includes(pageKey)) now.onBack(pageKey === ROOT ? '' : pageKey);
      }, 0);
    };
  }, [pageKey]);
  // Форма окна бота (ред. 9, 7.4.2): корень страницы `.bw-page`; снимается при первом кадре с данными.
  const shapeName = pageKey === ROOT ? 'bot-window' : 'bot-window-page';
  useShapeRecord(shapeName, !!page && !page.loading);
  if (!page) return null;
  return (
    <div className="bw-page" data-shape={page.loading ? undefined : shapeName}>
      {page.hero ? <Hero hero={page.hero} /> : null}
      {/* Заглушка формы окна (ред. 9 §2.3) вместо ручной плашки 138. */}
      {page.loading ? <Shape name={shapeName} groupsClass="" groupClass="k-lgroup" /> : null}
      {bcastWebLayout(page) ? (
        <BcastRootBody blocks={page.blocks} title={page.title} />
      ) : (
        page.blocks.map((b, i) => <Block key={`${b.kind}:${i}`} block={b} />)
      )}
    </div>
  );
}

/**
 * СТРАНИЦА РАССЫЛКИ НА ТЕЛЕФОНЕ В БРАУЗЕРЕ — В ПОРЯДКЕ РОДНОЙ (П13, кадр iOS
 * R07-broadcast-analytics: «Отправлено» → «Запуски» → «Остановить», «Редактировать» →
 * график без легенды → сноска). Блоки собирает общий мозг окна бота (bot-window-native),
 * их же читают старые сборки приложения — состав НЕ меняется. Здесь меняется только
 * ПОРЯДОК ОТРИСОВКИ: блок «runs» встаёт после кнопок запуска (`part="rest"`: график без легенды, журнал;
 * карточки «Запуски · N за неделю» нет — APP-CLAIMS-1020 ред. 6, П26.1, вопрос 83), а
 * «Редактировать» берётся пунктом `edit` из того же меню «⋯» карточки — второй руки
 * перехода в редактор нет. Узнаём страницу рассылки по составу: корень, первым — цифры
 * (у бота цифры стоят под графиком, у цепочки первым — её свой блок).
 */
function bcastWebLayout(page: NativeStatsPage): boolean {
  // Широкий экран — тот же порядок (Д1.4, Закон 5 п.9): окно рассылки в панели 460 = телефон. Рассылку узнаём по
  // составу: первым — цифры, или кнопки отчёта `bc:*` (неотправленная — «Отправить», отправленная — «Создать дубликат»).
  return (
    page.key === ROOT &&
    !isAppClient() &&
    (page.blocks[0]?.kind === 'stats' ||
      page.blocks.some((b) => b.kind === 'button' && !!b.key?.startsWith('bc:')))
  );
}

/** Отчёт рассылки (APP-CLAIMS-1020 ред. 7, Р2), порядок как в приложении: письмо пузырями переписки (тот же показ, что
 *  «Чаты» — `FeedBubbles`) → цифры или «Рассылка не отправлялась» → кнопки (отправленная — одна «Создать дубликат»,
 *  неотправленная — «Отправить» и «Редактировать») → «Отправки». */
function BcastRootBody({ blocks, title }: { blocks: readonly NativeStatsBlock[]; title: string }) {
  useMenuTick();
  const bubbles = useBotWindowBubbles();
  const runs = blocks.find((b) => b.kind === 'runs') ?? null;
  const stats = blocks.filter((b) => b.kind === 'stats');
  const notes = blocks.filter((b) => b.kind === 'note');
  // «Редактировать» — пунктом `edit` меню «⋯» карточки (вторая рука перехода в редактор не заводится); мозг просит его
  // блоком `bc:edit` — только у неотправленной.
  const wantEdit = blocks.some((b) => b.kind === 'button' && b.key === 'bc:edit');
  const acts = blocks.filter((b) => b.kind === 'error' || (b.kind === 'button' && b.key?.startsWith('bc:') && b.key !== 'bc:edit'));
  const rest = blocks.filter((b) => !stats.includes(b) && !notes.includes(b) && !acts.includes(b) && b !== runs && !(b.kind === 'button' && b.key === 'bc:edit'));
  const m = webMenu?.() ?? null;
  const edit = wantEdit ? (m?.sections.flatMap((sec) => sec.items).find((it) => it.key === 'edit') ?? null) : null;
  const draftId = bubbles[0]?.draftId ?? '';
  return (
    <>
      {bubbles.length > 0 ? <FeedBubbles className="bw-bc-msg" messages={bubbles} names={draftId ? { [draftId]: title } : {}} /> : null}
      {stats.map((b, i) => (
        <BcastStat key={`st:${i}`} block={b} />
      ))}
      {notes.map((b, i) => (
        <Block key={`note:${i}`} block={b} />
      ))}
      <div className="bw-bc-acts">
        {acts.map((b, i) => (
          <Block key={`act:${i}`} block={b} />
        ))}
        {edit && m ? (
          <KListBlock>
            <KButton action onClick={() => m.onPick('edit')}>
              {edit.label}
            </KButton>
          </KListBlock>
        ) : null}
      </div>
      {runs ? <Runs block={runs} part="rest" /> : null}
      {rest.map((b, i) => (
        <Block key={`rest:${b.kind}:${i}`} block={b} />
      ))}
    </>
  );
}

/** Цифра рассылки как в родной: имя подписью над карточкой, значение словами в ней. */
function BcastStat({ block }: { block: NativeStatsBlock }) {
  return (
    <>
      {(block.cards ?? []).map((c, i) => (
        <KListSection key={i} label={c.label} {...(c.hint ? { hint: c.hint } : {})}>
          <KListGroup>
            <KListRow title={c.value} />
          </KListGroup>
        </KListSection>
      ))}
    </>
  );
}

// ── Герой ──────────────────────────────────────────────────────────────────────────────

/** Рисунки героя по имени SF-символа приложения (линия 2, как у значков кабинета). */
const HERO_ICONS: Record<string, ReactNode> = {
  // `chart.bar` 56 light: три столбика контуром, рисунок 66 × 52 (кадр приложения app-2.png).
  'chart.bar': (
    <>
      <rect x="1.5" y="25" width="16" height="25.5" rx="4" />
      <rect x="25" y="13" width="16" height="37.5" rx="4" />
      <rect x="48.5" y="1.5" width="16" height="49" rx="4" />
    </>
  ),
};

function Hero({ hero }: { hero: NativeAccountHero }) {
  const glyph = hero.icon ? HERO_ICONS[hero.icon] : null;
  // Герой — ОБЩИЙ герой кабинета (`SettingsHero`, зеркало ScrHero приложения): своей
  // разметки у окна бота нет. Рисунок 66 × 52 и фото приходят готовыми (`set-hero-raw`).
  const icon = hero.photo ? (
    // eslint-disable-next-line @next/next/no-img-element -- готовый снимок base64 от мозга окна
    <img className="set-hero-photo" src={hero.photo} alt="" width={72} height={72} decoding="async" />
  ) : glyph ? (
    <svg
      className="set-hero-raw"
      viewBox="0 0 66 52"
      fill="none"
      stroke="currentColor"
      strokeWidth={3}
      strokeLinejoin="round"
      style={{ color: hero.color }}
      aria-hidden
    >
      {glyph}
    </svg>
  ) : null;
  return (
    <SettingsHero icon={icon} title={hero.title} {...(hero.meta ? { meta: hero.meta } : {})} />
  );
}

// ── Блоки ──────────────────────────────────────────────────────────────────────────────

function Block({ block }: { block: NativeStatsBlock }) {
  switch (block.kind) {
    case 'hero':
      return block.hero ? <Hero hero={block.hero} /> : null;
    case 'fields':
      return <Fields block={block} />;
    case 'note':
      return block.text ? <KListNote>{block.text}</KListNote> : null;
    case 'error':
      return block.text ? <FFormError>{block.text}</FFormError> : null;
    case 'button':
      return block.key ? (
        <KListBlock>
          <KButton
            action
            tone={
              block.tone === 'accent' ? 'primary' : block.tone === 'danger' ? 'danger' : undefined
            }
            loading={block.busy}
            onClick={() => tap(block.key!)}
          >
            {block.label}
          </KButton>
        </KListBlock>
      ) : null;
    case 'periods':
      return block.items && block.items.length > 0 ? (
        <KListBlock>
          <KSegment
            value={block.current ?? ''}
            options={block.items.map((it) => ({ value: it.key, label: it.label }))}
            onChange={(v) => tap(`period:${v}`)}
          />
        </KListBlock>
      ) : null;
    case 'stats':
      return <StatsCards block={block} />;
    case 'runs':
      return <Runs block={block} />;
    case 'events':
      return <Events block={block} />;
    case 'facts':
      return (
        <div className="bw-facts">
          {block.when ? <div className="bw-facts-t">{block.when}</div> : null}
          {(block.tags ?? []).length > 0 ? (
            <div className="bw-tags">
              {(block.tags ?? []).map((tg, i) => (
                <span key={i} className={`bw-tag bw-tag--${tg.tone}`}>
                  {tg.text}
                </span>
              ))}
            </div>
          ) : null}
        </div>
      );
    case 'steps':
      return <Steps block={block} />;
    default:
      return null;
  }
}

function StatsCards({ block }: { block: NativeStatsBlock }) {
  const cards = block.cards ?? [];
  if (cards.length === 0) return null;
  return (
    <KListSection>
      <KListGroup>
        {cards.map((c, i) => (
          <KListRow key={i} title={c.label} value={c.value} {...(c.hint ? { hint: c.hint } : {})} />
        ))}
      </KListGroup>
    </KListSection>
  );
}

/**
 * ГРАФИК ОКНА БОТА = П12 ПРИЛОЖЕНИЯ (APP-CLAIMS-1020 ред. 6, П26.1): три пилюли «Запуски · N», «Контакты · N»,
 * «Конверсия · N %» с мультивыбором (тап — statsTap 'series:<key>', выбор держит мозг окна; последнюю включённую мозг
 * не снимает), под ними итог и график включённых серий (столбики и линия конверсии — DayBars).
 */
function SeriesChart({ block }: { block: NativeStatsBlock }) {
  const ta = useTranslations('cabinet.automations');
  const series = block.series ?? [];
  const on = new Set(block.seriesOn ?? []);
  const shown = series.filter((x) => on.has(x.key));
  const days = (block.tips ?? []).map((x) => x.day);
  return (
    <>
      <div className="bw-pills" role="group">
        {series.map((x) => (
          <KBareButton
            key={x.key}
            className={`bw-pill${on.has(x.key) ? ' is-on' : ' k-glass'}`}
            aria-pressed={on.has(x.key)}
            data-series={x.key}
            onClick={() => tap(`series:${x.key}`)}
          >
            {`${x.word} · ${x.total}`}
          </KBareButton>
        ))}
      </div>
      <div className="bw-chart bw-chart--bars">
        <DayBars
          series={shown}
          days={days}
          scrubText={(day, value) => ta('chartScrub', { day, value })}
        />
      </div>
    </>
  );
}

/** Линии графика готовыми точками: своей шкалы и своего счёта у листа нет. */
function Lines({ block, noLegend = false }: { block: NativeStatsBlock; noLegend?: boolean }) {
  if ((block.series ?? []).length > 0) return <SeriesChart block={block} />;
  const line = block.line ?? [];
  if (line.length < 2) return null;
  const W = 320;
  const H = 96;
  const path = (pts: number[], max: number): string =>
    pts
      .map(
        (v, i) =>
          `${i === 0 ? 'M' : 'L'}${((i / (pts.length - 1)) * W).toFixed(1)} ${(H - (max > 0 ? v / max : 0) * (H - 6) - 3).toFixed(1)}`,
      )
      .join(' ');
  const max1 = Math.max(1, ...line, ...(block.line2 && !block.line2Max ? block.line2 : []));
  return (
    <div className="bw-chart">
      {!noLegend && (block.lineName || block.line2Name || block.line3Name) ? (
        <div className="bw-legend">
          {block.lineName ? (
            <span>
              <i style={block.lineInk ? { background: block.lineInk } : undefined} />
              {block.lineName}
            </span>
          ) : null}
          {block.line2Name ? (
            <span>
              <i
                className="l2"
                style={block.line2Ink ? { background: block.line2Ink } : undefined}
              />
              {block.line2Name}
            </span>
          ) : null}
          {block.line3Name ? (
            <span>
              <i
                className="l3"
                style={block.line3Ink ? { background: block.line3Ink } : undefined}
              />
              {block.line3Name}
            </span>
          ) : null}
        </div>
      ) : null}
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" aria-hidden>
        <path
          className="bw-l1"
          d={path(line, max1)}
          style={block.lineInk ? { stroke: block.lineInk } : undefined}
        />
        {block.line2 && block.line2.length === line.length ? (
          <path
            className="bw-l2"
            d={path(block.line2, block.line2Max ?? max1)}
            style={block.line2Ink ? { stroke: block.line2Ink } : undefined}
          />
        ) : null}
        {block.line3 && block.line3.length === line.length ? (
          <path
            className="bw-l3"
            d={path(block.line3, block.line3Max ?? max1)}
            style={block.line3Ink ? { stroke: block.line3Ink } : undefined}
          />
        ) : null}
      </svg>
      <div className="bw-scale">
        <span>{block.lineMin}</span>
        <span>{block.lineMax}</span>
      </div>
    </div>
  );
}

/** `part="rest"` — страница РАССЫЛКИ на телефоне в браузере: график без легенды и журнал. Без `part` — весь блок
 *  (боты, цепочки, широкий экран).
 *  КАРТОЧКИ «ЗАПУСКИ · N ЗА НЕДЕЛЮ» С ПЛАШКАМИ НЕТ (APP-CLAIMS-1020 ред. 6, П26.1, вопрос 83): она делалась под старый
 *  кадр iOS (Р14); окно приложения сейчас идёт сразу с графика (BotWindowScreen.addRuns — пилюли и график). */
function Runs({ block, part }: { block: NativeStatsBlock; part?: 'rest' }) {
  const groups = (block.groups ?? []) as Array<{ day: string; items: NativeStatsRun[] }>;
  return <RunsRest block={block} groups={groups} noLegend={part === 'rest'} />;
}

/** Хвост блока «Запуски»: график, пусто/скелетон, журнал по дням, «Ещё». */
function RunsRest({
  block,
  groups,
  noLegend,
}: {
  block: NativeStatsBlock;
  groups: Array<{ day: string; items: NativeStatsRun[] }>;
  noLegend: boolean;
}) {
  return (
    <>
      <Lines block={block} noLegend={noLegend} />
      {block.loading ? <div className="bw-skel-box"><Sk w="100%" h={138} r="r" /></div> : null}
      {groups.length === 0 && block.empty ? <KListNote>{block.empty}</KListNote> : null}
      {groups.map((g) => (
        <KListSection key={g.day} label={g.day}>
          <KListGroup>
            {g.items.map((r) => (
              <KListRow
                key={r.id}
                title={
                  <span className="bw-run-t">
                    {r.title}
                    {r.bad ? <i className="bw-dot" aria-hidden /> : null}
                    {r.tag ? <em>{r.tag}</em> : null}
                  </span>
                }
                hint={[r.meta, r.why].filter(Boolean).join(' · ')}
                chevron
                onClick={() => tap(`run:${r.id}`)}
              />
            ))}
          </KListGroup>
        </KListSection>
      ))}
      {block.more ? (
        <KListBlock>
          <KButton block onClick={() => tap(block.key ?? 'more')}>
            {block.more}
          </KButton>
        </KListBlock>
      ) : null}
    </>
  );
}

function Events({ block }: { block: NativeStatsBlock }) {
  const groups = (block.groups ?? []) as Array<{ day: string; items: NativeStatsEvent[] }>;
  return (
    <>
      {block.loading ? <div className="bw-skel-box"><Sk w="100%" h={138} r="r" /></div> : null}
      {groups.length === 0 && block.hero ? (
        // Р7 ред. 7: пустая лента — герой (тот же KEmpty, что пустой список ботов); серой строки при нём нет.
        <KEmpty className="bw-ev-hero" icon={<IconContacts />} title={block.hero.title} note={block.hero.meta ?? ''} />
      ) : groups.length === 0 && block.empty ? (
        <KListNote>{block.empty}</KListNote>
      ) : null}
      {groups.map((g) => (
        <KListSection key={g.day} label={g.day}>
          <KListGroup>
            {g.items.map((ev, i) => (
              <KListRow
                key={`${ev.key}:${i}`}
                className="bw-ev"
                icon={
                  ev.photo ? (
                    // eslint-disable-next-line @next/next/no-img-element -- готовый снимок base64 от мозга окна
                    <img
                      src={
                        ev.photo.startsWith('data:')
                          ? ev.photo
                          : `data:image/png;base64,${ev.photo}`
                      }
                      alt=""
                      width={32}
                      height={32}
                      decoding="async"
                    />
                  ) : (
                    <span className="bw-ev-i" style={{ background: ev.initialColor }}>
                      {ev.initial}
                    </span>
                  )
                }
                title={ev.title}
                hint={ev.text}
                value={ev.time}
                {...(ev.key ? { onClick: () => tap(ev.key) } : {})}
              />
            ))}
          </KListGroup>
        </KListSection>
      ))}
      {block.more ? (
        <KListBlock>
          <KButton block onClick={() => tap(block.key ?? 'events:more')}>
            {block.more}
          </KButton>
        </KListBlock>
      ) : null}
    </>
  );
}

function Steps({ block }: { block: NativeStatsBlock }) {
  const steps = block.steps ?? [];
  if (steps.length === 0) return block.empty ? <KListNote>{block.empty}</KListNote> : null;
  return (
    <KListSection>
      <KListGroup>
        {steps.map((s, i) => (
          <KListRow
            key={i}
            title={
              <span className="bw-run-t">
                {s.name}
                {s.status === 'error' ? <i className="bw-dot" aria-hidden /> : null}
              </span>
            }
            hint={[s.why, s.file].filter(Boolean).join(' · ')}
            value={s.dur}
          />
        ))}
      </KListGroup>
    </KListSection>
  );
}

// ── Пузырь строк ───────────────────────────────────────────────────────────────────────

function Fields({ block }: { block: NativeStatsBlock }) {
  const fields = block.fields ?? [];
  if (fields.length === 0) return null;
  return (
    <KListSection
      {...(block.label ? { label: block.label } : {})}
      {...(block.hint ? { hint: block.hint } : {})}
    >
      <KListGroup>
        {fields.map((f) => (
          <FieldRow
            key={f.key}
            field={f}
            folders={block.folders?.key === f.key ? block.folders : undefined}
          />
        ))}
      </KListGroup>
    </KListSection>
  );
}

/** Значок сети возле значения — тот же, что у строк приложения (`valueImage`). */
function valueOf(f: NativeAccountField): ReactNode {
  const chan =
    f.valueImage === 'ChanTG' ? 'telegram' : f.valueImage === 'ChanIG' ? 'instagram' : '';
  if (!chan) return f.value ?? '';
  return (
    <KListValueIcon icon={<ChannelIcon channel={chan} size={14} />} tint={channelHex(chan)}>
      {f.value ?? ''}
    </KListValueIcon>
  );
}

function FieldRow({
  field: f,
  folders,
}: {
  field: NativeAccountField;
  folders?: NativeStatsFolders | undefined;
}) {
  const panel = useSlidePanel();
  const tc = useTranslations('cabinet');
  const hint = f.hint ? { hint: f.hint } : {};
  switch (f.kind) {
    case 'value':
      return <KListRow title={f.title} value={valueOf(f)} {...hint} />;
    case 'switch': {
      const row = (
        <KListRow
          control
          title={f.title}
          {...hint}
          value={
            f.wait ? (
              // Положение ещё не подтверждено сервером (SERVER_TRUTH плана прогрева) — место тумблера пустое.
              <span className="bw-sw-wait" aria-hidden />
            ) : (
              <KSwitch
                checked={!!f.on}
                disabled={!!f.disabled}
                ariaLabel={f.title}
                onChange={(v) => set(f.key, v ? '1' : '0')}
              />
            )
          }
        />
      );
      return f.key === BOT_ON_KEY ? <BotOnRow>{row}</BotOnRow> : row;
    }
    case 'check':
      return (
        <KListRow
          title={f.title}
          {...hint}
          value={f.on ? <KListCheck /> : ''}
          disabled={!!f.disabled}
          onClick={() => set(f.key, f.on ? '0' : '1')}
        />
      );
    case 'nav':
      return (
        <KListRow
          title={f.title}
          value={f.value ?? ''}
          {...hint}
          chevron
          disabled={!!f.disabled}
          onClick={() => tap(f.key)}
        />
      );
    case 'button':
      return (
        <KListRow
          title={f.title}
          {...hint}
          {...(f.tone === 'danger'
            ? { tone: 'danger' as const }
            : f.tone === 'accent'
              ? { tone: 'accent' as const }
              : {})}
          disabled={!!f.disabled}
          onClick={() => tap(f.key)}
        />
      );
    case 'text':
      return (
        <KListRow
          title={f.title}
          value={f.value ?? ''}
          {...hint}
          chevron
          disabled={!!f.disabled}
          onClick={() => {
            const submit: { current: () => void } = { current: () => {} };
            // ЛИСТ-СТОПКА ВО ВЕСЬ РОСТ с «×» и «✓» (кадры iOS B09-botwin-name/-desc, ТЗ
            // WEB-PARITY-3 Б16–Б18): окно бота под ним уходит колодой; прежний низкий лист снят.
            panel.open({
              title: f.title,
              sheet: true,
              deck: true,
                            // «×» — новый лист поверх окна (кадр iOS B09-botwin-name; ТЗ WEB-CAUSES М83, доработка).
              lead: 'close',
              winKey: `bot-window-edit:${f.key}`,
              actionLabel: f.title,
              actionIcon: 'checkmark',
              onAction: () => submit.current(),
              body: <EditBody field={f} submitRef={submit} onDone={() => panel.back()} />,
            });
          }}
        />
      );
    case 'choice':
      return (
        <KListRow
          title={f.title}
          value={f.value ?? ''}
          {...hint}
          chevron
          disabled={!!f.disabled}
          onClick={() =>
            // Лист выбора — тоже стопкой во весь рост с «×» (кадры B10/B11/B11-botwin-term);
            // у выбора нескольких (линии графика) — «✓» в шапке применяет и закрывает.
            panel.open({
              title: folders?.title ?? f.title,
              sheet: true,
              deck: true,
                            // «×» — новый лист поверх окна (кадры iOS B10/B11; ТЗ WEB-CAUSES М83, доработка).
              lead: 'close',
              // Полулист (кадры iOS B10-botwin-folder / B11-botwin-term: лист до середины экрана).
              sheetDetent: 0.5,
              winKey: `bot-window-pick:${f.key}`,
              ...(f.multi
                ? { actionLabel: tc('done'), actionIcon: 'checkmark', onAction: () => panel.back() }
                : {}),
              body: folders ? (
                <FoldersBody fieldKey={f.key} onDone={() => panel.back()} />
              ) : (
                <PickBody fieldKey={f.key} onDone={() => panel.back()} />
              ),
            })
          }
        />
      );
    case 'field':
      return <InlineField field={f} />;
    default:
      return null;
  }
}

/** Поле прямо в строке (`field`): правится на месте, каждая правка уходит мозгу. */
function InlineField({ field: f }: { field: NativeAccountField }) {
  const [v, setV] = useState(f.raw ?? f.value ?? '');
  useEffect(() => setV(f.raw ?? f.value ?? ''), [f.raw, f.value]);
  const change = (next: string): void => {
    const cut = f.max ? next.slice(0, f.max) : next;
    setV(cut);
    set(f.key, cut);
  };
  return (
    <KListField>
      {f.multiline ? (
        <KTextarea
          value={v}
          rows={3}
          placeholder={f.placeholder ?? ''}
          aria-label={f.title || f.placeholder}
          onChange={(e) => change(e.target.value)}
        />
      ) : (
        <KInput
          value={v}
          placeholder={f.placeholder ?? ''}
          aria-label={f.title || f.placeholder}
          onChange={(e) => change(e.target.value)}
        />
      )}
    </KListField>
  );
}

/** Найти живую строку по ключу: листы выбора читают свежие данные мозга, а не снимок. */
function useField(key: string): {
  field: NativeAccountField | null;
  folders: NativeStatsFolders | null;
} {
  const st = useStatsWeb();
  for (const p of st?.pages ?? []) {
    for (const b of p.blocks) {
      const f = (b.fields ?? []).find((x) => x.key === key);
      if (f) return { field: f, folders: b.folders?.key === key ? b.folders : null };
    }
  }
  return { field: null, folders: null };
}

/** Лист правки одного слова: поле и «✓» в шапке (в приложении — родной лист с полем). */
function EditBody({
  field: f,
  submitRef,
  onDone,
}: {
  field: NativeAccountField;
  submitRef: { current: () => void };
  onDone: () => void;
}) {
  const ts = useTranslations('cabinet.scenario');
  const [v, setV] = useState(f.raw ?? f.value ?? '');
  const [bad, setBad] = useState('');
  const empty = (f as NativeAccountField & { emptyError?: string }).emptyError;
  submitRef.current = () => {
    if (empty && v.trim() === '') {
      setBad(empty);
      return;
    }
    set(f.key, v);
    onDone();
  };
  const change = (next: string): void => {
    setBad('');
    setV(f.max ? next.slice(0, f.max) : next);
  };
  return (
    <div className="bw-page">
      {f.multiline ? (
        /* Многострочное — высокая карточка 200 с текстом 17 (кадр iOS B09-botwin-desc:
           `AccountEditSheet` multiline, поля 16): та же карточка набора, что у поля в строке,
           класс `bw-edit-area` даёт только рост 200. */
        <KListGroup>
          {bad ? <FFormError>{bad}</FFormError> : null}
          <div className="bw-edit-area">
            <KListField>
              <KTextarea
                autoFocus
                value={v}
                rows={5}
                placeholder={f.placeholder ?? ''}
                aria-label={f.title}
                onChange={(e) => change(e.target.value)}
              />
            </KListField>
          </div>
        </KListGroup>
      ) : (
        /* Одно слово — поле-карточка с «×» очистки (FClearField, как у «Имени» профиля; кадр iOS
           B09-botwin-name): карточку даёт KListGroup, как на профиле. */
        <KListGroup>
          {bad ? <FFormError>{bad}</FFormError> : null}
          <FClearField
            autoFocus
            value={v}
            placeholder={f.placeholder ?? ''}
            ariaLabel={f.title}
            onChange={change}
            onEnter={() => submitRef.current()}
          />
        </KListGroup>
      )}
      {/* Капсула «Сохранить» под полем — родной AccountEditSheet (кадры iOS B09-botwin-name /
          B09-botwin-desc): --accent во всю ширину, 52, верх в 56 под карточкой, когда под ней
          держится место строки ошибки (emptyError), иначе в 16. Делает то же, что «✓» шапки. */}
      <KButton tone="primary" action className={`bw-edit-done${empty ? '' : ' is-tight'}`} onClick={() => submitRef.current()}>
        {f.done || ts('aboutSave')}
      </KButton>
    </div>
  );
}

/** Лист выбора одного из вариантов: галочка у выбранного (в приложении — родной лист). */
function PickBody({ fieldKey, onDone }: { fieldKey: string; onDone: () => void }) {
  const { field: f } = useField(fieldKey);
  if (!f) return null;
  const act = (f as NativeAccountField & { pickAction?: { label: string; value: string } })
    .pickAction;
  const picked = f.multi ? new Set((f.picked ?? '').split(',').filter(Boolean)) : null;
  return (
    // Выбор нескольких — родной `WizPickSheet` (multiple): над карточкой ещё 12 (`Pad.row`),
    // у одиночного `AccountPickSheet` — нет (кадры B11-botwin-lines / B11-botwin-term).
    <div className={`bw-page${picked ? ' bw-pick-multi' : ''}`}>
      <KListGroup>
        {(f.options ?? []).map((o) => {
          const on = picked ? picked.has(o.value) : (f.picked ?? '') === o.value;
          return (
            <KListRow
              key={o.value}
              title={o.label}
              {...(o.hint ? { hint: o.hint } : {})}
              value={on ? <KListCheck /> : ''}
              onClick={() => {
                if (picked) {
                  if (on) picked.delete(o.value);
                  else picked.add(o.value);
                  set(f.key, [...picked].join(','));
                  return;
                }
                set(f.key, o.value);
                onDone();
              }}
            />
          );
        })}
        {act ? (
          <KListRow
            title={act.label}
            tone="accent"
            onClick={() => {
              onDone();
              set(f.key, act.value);
            }}
          />
        ) : null}
      </KListGroup>
    </div>
  );
}

/** Лист папки «как метки» (П158 п.4): папки клиента, «Добавить папку»; П11 — мультивыбор (`pickedAll`). */
function FoldersBody({ fieldKey, onDone }: { fieldKey: string; onDone: () => void }) {
  const { folders: fo } = useField(fieldKey);
  if (!fo) return null;
  // «ДОБАВИТЬ ПАПКУ» — ОТДЕЛЬНОЙ КАРТОЧКОЙ НАД СПИСКОМ, имя — окном-вопросом с полем (кадры
  // iOS B10-botwin-folder / B10-botwin-folder-add, ТЗ WEB-PARITY-3 Б19–Б20): одно окно ввода
  // слова на кабинет (askPrompt), своего поля под списком больше нет.
  const add = (): void => {
    void askPrompt({ title: fo.add, placeholder: fo.addPh, confirmLabel: fo.addBtn }).then(
      (name) => {
        if (name) tap(`about:cat:add:${name}`);
      },
    );
  };
  return (
    // Родной лист папки — системная таблица `insetGrouped` (TagListSheet): свои поля
    // (класс `bw-folders`, кадр B10-botwin-folder).
    <div className="bw-page bw-folders">
      <KListGroup>
        <KListRow title={fo.add} tone="accent" onClick={add} />
      </KListGroup>
      {fo.pickedAll ? (
        // ПАПОК У БОТА НЕСКОЛЬКО (APP-CLAIMS-1020 ред. 3, П11, как на телефоне): галочка включает и выключает папку,
        // лист остаётся открытым; «Без папки» нет — пустой выбор и есть без папки. Запись — мозг окна, сразу.
        <KListGroup>
          {fo.items.map((n) => (
            <KListRow
              key={n}
              title={n}
              value={fo.pickedAll?.includes(n) ? <KListCheck /> : ''}
              onClick={() => tap(`about:cat:toggle:${n}`)}
            />
          ))}
        </KListGroup>
      ) : (
        <KListGroup>
          <KListRow
            title={fo.none}
            value={fo.picked === '' ? <KListCheck /> : ''}
            onClick={() => {
              tap('about:cat:pick:');
              onDone();
            }}
          />
          {fo.items.map((n) => (
            <KListRow
              key={n}
              title={n}
              value={fo.picked === n ? <KListCheck /> : ''}
              onClick={() => {
                tap(`about:cat:pick:${n}`);
                onDone();
              }}
            />
          ))}
        </KListGroup>
      )}
      {fo.note ? <p className="k-lsec__foot">{fo.note}</p> : null}
    </div>
  );
}
